import type { ReactNode } from "react"
import { ArrowCounterClockwiseIcon, CheckCircleIcon } from "@phosphor-icons/react"

import { StatusBadge } from "~/components/design-system/components"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import { UserAvatar } from "~/components/reusable/users/UserAvatar"
import { StatusColor } from "~/constants/status"
import { useUserLookup } from "~/hooks/useUsers"
import { formatTimeAgo } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"

import {
  PENDING_LABEL,
  REVIEWED_LABEL,
  reviewState,
  tallyReviews,
  type ReviewTally,
  type VariantReview,
} from "./reviewStatus"

/** Avatar + name (email fallback) + when, like the saved-view hover card's audit line. */
function ReviewedBy({ review }: { review: VariantReview }) {
  const { getUserName, getUserEmail, getUserImage } = useUserLookup()
  const id = review.reviewedBy ?? ""
  const email = id ? getUserEmail(id) : ""
  const userName = id ? getUserName(id) : ""
  // `getUserName` falls back to the raw id for an unknown user; that is no name to show.
  const name = userName && userName !== id ? userName : email
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {name && (
        <UserAvatar size={16} name={name} email={email} picture={getUserImage(id) || undefined} />
      )}
      <span className="truncate">
        {name ? `${name} · ` : ""}Reviewed {formatTimeAgo(review.reviewedAt)}
      </span>
    </span>
  )
}

export function VariantReviewBadge({ review }: { review: VariantReview | undefined }) {
  return review ? (
    <StatusBadge color={StatusColor.Success} text={REVIEWED_LABEL} />
  ) : (
    <StatusBadge color={StatusColor.Neutral} text={PENDING_LABEL} />
  )
}

export function ReviewTallyBadge({ tally }: { tally: ReviewTally }) {
  return (
    <StatusBadge
      color={
        tally.total > 0 && tally.reviewed === tally.total
          ? StatusColor.Success
          : StatusColor.Neutral
      }
      text={`${formatNumber(tally.reviewed)}/${formatNumber(tally.total)} reviewed`}
    />
  )
}

/** Mounted only while the menu is open, so a closed cell pays for no user lookup. */
function ReviewMenuHeader({
  variantIds,
  reviews,
  tally,
}: {
  variantIds: string[]
  reviews: ReadonlyMap<string, VariantReview>
  tally: ReviewTally
}) {
  if (variantIds.length > 1)
    return `${formatNumber(tally.reviewed)} of ${formatNumber(tally.total)} reviewed`
  const review = reviews.get(variantIds[0]!)
  return review ? <ReviewedBy review={review} /> : "Pending review"
}

/**
 * Click-to-change for one variant, or every variant under a group row or in a selection. The menu's
 * header carries who reviewed it and when, since a gated trigger takes no nested tooltip.
 */
export function ReviewStatusMenu({
  variantIds,
  reviews,
  onSet,
  asChild = false,
  children,
}: {
  variantIds: string[]
  reviews: ReadonlyMap<string, VariantReview>
  onSet: (variantIds: string[], reviewed: boolean) => void
  asChild?: boolean
  children: ReactNode
}) {
  const tally = tallyReviews(variantIds, reviews)
  const state = reviewState(tally)
  const all = variantIds.length > 1 ? " all" : ""

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        requires="edit"
        asChild={asChild}
        className={asChild ? undefined : "cursor-pointer rounded-md outline-none"}
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel className="paragraph-sm font-normal text-fg-subtle">
          <ReviewMenuHeader variantIds={variantIds} reviews={reviews} tally={tally} />
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={state === "all"} onSelect={() => onSet(variantIds, true)}>
          <CheckCircleIcon />
          {`Mark${all} as reviewed`}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={state === "none"} onSelect={() => onSet(variantIds, false)}>
          <ArrowCounterClockwiseIcon />
          {`Mark${all} as pending`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
